/**
 * Formatter Utilities
 * Centralized formatting functions to avoid duplication across the codebase
 */

/**
 * Format price as USD currency
 * @example formatPrice(250000) => "$250,000"
 * @example formatPrice(0) => "$0"
 */
export function formatPrice(price?: number | null): string {
    if (price === null || price === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
    }).format(price);
}

/**
 * Format phone number to (XXX) XXX-XXXX format
 * @example formatPhoneNumber("1234567890") => "(123) 456-7890"
 */
export function formatPhoneNumber(phone: string | null | undefined): string {
    if (!phone) return '';

    const cleaned = phone.replace(/\D/g, '');

    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
        return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }

    return phone;
}

/** The digits (and a leading +) a dialer needs: "(609) 255-9635" → "6092559635". */
function dialableNumber(phone: string | null | undefined): string {
    return (phone || '').replace(/[^\d+]/g, '');
}

/** A `tel:` link for any typed phone format; "#" when there is no number. */
export function telHref(phone: string | null | undefined): string {
    const number = dialableNumber(phone);
    return number ? `tel:${number}` : '#';
}

/** An `sms:` link that opens the phone's messages app to this number; "#" when there is none. */
export function smsHref(phone: string | null | undefined): string {
    const number = dialableNumber(phone);
    return number ? `sms:${number}` : '#';
}

/**
 * Extract date string from ISO date (YYYY-MM-DD format)
 * @example getDateString("2024-01-15T10:30:00Z") => "2024-01-15"
 */
export function getDateString(date?: string | null): string {
    if (!date) return '';
    return date.split('T')[0] ?? '';
}

/**
 * Format date to human readable format
 * @example formatDate("2024-01-15") => "January 15, 2024"
 */
export function formatDate(date?: string | null, options?: Intl.DateTimeFormatOptions): string {
    if (!date) return 'N/A';

    const defaultOptions: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    };

    return new Date(date).toLocaleDateString('en-US', options ?? defaultOptions);
}

/**
 * Format date short (month short, day, year)
 * @example formatDateShort("2024-01-15") => "Jan 15, 2024"
 */
export function formatDateShort(date?: string | null): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
}

/**
 * Format date with time
 * @example formatDateTime("2024-01-15T10:30:00") => "Jan 15, 10:30 AM"
 */
export function formatDateTime(date?: string | null): string {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

/**
 * Format number with commas
 * @example formatNumber(1234567) => "1,234,567"
 */
export function formatNumber(num?: number | null): string {
    if (num === null || num === undefined) return 'N/A';
    return num.toLocaleString('en-US');
}

/**
 * Format square feet
 * @example formatSquareFeet(2500) => "2,500 sqft"
 */
export function formatSquareFeet(sqft?: number | null): string {
    if (!sqft) return 'N/A';
    return `${sqft.toLocaleString('en-US')} sqft`;
}

/**
 * Truncate text with ellipsis
 * @example truncateText("Long text here", 10) => "Long text..."
 */
export function truncateText(text: string, maxLength: number): string {
    if (text.length <= maxLength) return text;
    return `${text.slice(0, maxLength)}...`;
}

/**
 * Get initials from name
 * @example getInitials("John Doe") => "JD"
 */
export function getInitials(name: string): string {
    return name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
}

const UNNAMED_LABEL = 'Unnamed';

/** "First Last" with either part optional; '' when neither is set. */
function joinPersonName(firstName?: string | null, lastName?: string | null): string {
    return [firstName?.trim(), lastName?.trim()].filter(Boolean).join(' ');
}

/**
 * Get full name from first and last name
 * @example getFullName("John", "Doe") => "John Doe"
 */
export function getFullName(firstName?: string | null, lastName?: string | null): string {
    return joinPersonName(firstName, lastName) || UNNAMED_LABEL;
}

/** The name fields a contact row carries, whichever API shape it came from. */
export interface ContactNameFields {
    display_name?: string | null;
    company_name?: string | null;
    first_name?: string | null;
    last_name?: string | null;
}

/**
 * The name a contact is shown under. The backend's `display_name` already
 * prefers the company name for a company; without it, a company-only
 * contact (no person's name) is called by its company and a person by
 * first + last name.
 * @example contactDisplayName({ company_name: "LeadGen Co" }) => "LeadGen Co"
 * @example contactDisplayName({ first_name: "Ann", last_name: "Lee" }) => "Ann Lee"
 */
export function contactDisplayName(contact: ContactNameFields, fallback: string = UNNAMED_LABEL): string {
    return contact.display_name?.trim()
        || contact.company_name?.trim()
        || joinPersonName(contact.first_name, contact.last_name)
        || fallback;
}

// ===========================================
// Email Utilities
// ===========================================

/**
 * Spam domain list for email validation
 */
const SPAM_DOMAINS = [
    'tempmail',
    'guerrillamail',
    'mailinator',
    'throwaway',
    '10minutemail',
    'fakeinbox',
    'trashmail',
    'getnada',
];

/**
 * Check if email is from a known spam/temporary email domain
 * @example isSpamEmail("test@tempmail.com") => true
 */
export function isSpamEmail(email: string): boolean {
    const domain = email.split('@')[1]?.toLowerCase() || '';
    return SPAM_DOMAINS.some((spam) => domain.includes(spam));
}

/**
 * Email provider info type
 */
export interface EmailProviderInfo {
    name: string;
    color: string;
}

/**
 * Get email provider info from email address
 * @example getEmailProvider("test@gmail.com") => { name: "Gmail", color: "text-red-500" }
 */
export function getEmailProvider(email: string | null): EmailProviderInfo | null {
    if (!email) return null;

    const domain = email.split('@')[1]?.toLowerCase();

    if (domain?.includes('gmail')) {
        return { name: 'Gmail', color: 'text-red-500' };
    }
    if (domain?.includes('outlook') || domain?.includes('hotmail') || domain?.includes('live')) {
        return { name: 'Outlook', color: 'text-blue-500' };
    }
    if (domain?.includes('yahoo')) {
        return { name: 'Yahoo', color: 'text-purple-500' };
    }
    if (domain?.includes('icloud') || domain?.includes('me.com')) {
        return { name: 'iCloud', color: 'text-gray-500' };
    }

    return { name: 'Email', color: 'text-gray-500' };
}

/**
 * Format file size to human readable format
 * @example formatFileSize(1024) => "1 KB"
 * @example formatFileSize(1048576) => "1 MB"
 */
export function formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}
